import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { JsonEditor } from '../../_shared/components/JsonEditor';
import { upstreamFormSchema, UPSTREAM_ALGORITHMS } from '../schema/upstreamSchema';
import { useCreateUpstream, useReplaceUpstream } from '../hooks/useUpstreams';
import type { UpstreamFormValues } from '../schema/upstreamSchema';
import type { UpstreamRow } from '../types/upstream';

interface UpstreamFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: UpstreamRow | null;
  onClose: () => void;
  onSaved?: () => void;
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: 'var(--spacing-2) var(--spacing-3)',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
        background: 'none',
        color: active ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

const DEFAULT_VALUES: UpstreamFormValues = {
  name: '',
  description: '',
  algorithm: 'ROUND_ROBIN',
  hashOn: '',
  healthcheckActive: false,
  healthcheckPassive: false,
  enabled: true,
};

export function UpstreamFormDialog({ open, mode, initial, onClose, onSaved }: UpstreamFormDialogProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'basic' | 'healthcheck' | 'json'>('basic');
  const [toast, setToast] = useState<string | null>(null);

  const form = useForm<UpstreamFormValues>({
    resolver: zodResolver(upstreamFormSchema),
    defaultValues: initial
      ? {
          name: initial.name,
          description: initial.description ?? '',
          algorithm: initial.algorithm,
          hashOn: initial.hashOn ?? '',
          healthcheckActive: initial.healthcheckActive,
          healthcheckPassive: initial.healthcheckPassive,
          enabled: initial.enabled,
        }
      : DEFAULT_VALUES,
  });

  const createUpstream = useCreateUpstream((name) => {
    setToast(t('gateway.upstreams.toast.created', { name }));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });
  const replaceUpstream = useReplaceUpstream(initial?.id ?? 0, () => {
    setToast(t('gateway.upstreams.toast.updated'));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });

  useEffect(() => {
    if (open) {
      setActiveTab('basic');
      form.reset(initial
        ? {
            name: initial.name,
            description: initial.description ?? '',
            algorithm: initial.algorithm,
            hashOn: initial.hashOn ?? '',
            healthcheckActive: initial.healthcheckActive,
            healthcheckPassive: initial.healthcheckPassive,
            enabled: initial.enabled,
          }
        : DEFAULT_VALUES);
    }
  }, [open, initial, form]);

  const watchedValues = form.watch();
  const isPending = createUpstream.isPending || replaceUpstream.isPending;

  const onSubmit = form.handleSubmit((values) => {
    if (mode === 'create') {
      createUpstream.mutate(values);
    } else if (initial) {
      replaceUpstream.mutate(values);
    }
  });

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    boxSizing: 'border-box',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    marginBottom: 'var(--spacing-1)',
  };

  const fieldStyle: React.CSSProperties = { marginBottom: 'var(--spacing-3)' };

  return (
    <FormDialog
      open={open}
      title={mode === 'create' ? t('gateway.upstreams.form.create') : t('gateway.upstreams.form.edit')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={isPending ? t('gateway.shared.actions.saving') : t('gateway.shared.actions.save')}
      cancelLabel={t('gateway.shared.actions.cancel')}
    >
      {toast && (
        <div role="alert" style={{ marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-success)', color: 'var(--color-text-inverse)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
          {toast}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--color-surface-border)', marginBottom: 'var(--spacing-4)' }}>
        <TabButton active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>{t('gateway.upstreams.form.tabs.basic')}</TabButton>
        <TabButton active={activeTab === 'healthcheck'} onClick={() => setActiveTab('healthcheck')}>{t('gateway.upstreams.form.tabs.healthcheck')}</TabButton>
        <TabButton active={activeTab === 'json'} onClick={() => setActiveTab('json')}>JSON</TabButton>
      </div>

      {/* Basic Tab */}
      {activeTab === 'basic' && (
        <div role="tabpanel">
          <div style={fieldStyle}>
            <label htmlFor="upstream-name" style={labelStyle}>{t('gateway.upstreams.form.name.label')}</label>
            <input
              id="upstream-name"
              {...form.register('name')}
              placeholder="my-upstream"
              aria-invalid={!!form.formState.errors.name}
              aria-describedby={form.formState.errors.name ? 'upstream-name-error' : undefined}
              style={inputStyle(!!form.formState.errors.name)}
            />
            {form.formState.errors.name && (
              <span id="upstream-name-error" role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                {form.formState.errors.name.message}
              </span>
            )}
          </div>

          <div style={fieldStyle}>
            <label htmlFor="upstream-description" style={labelStyle}>{t('gateway.upstreams.form.description.label')}</label>
            <input
              id="upstream-description"
              {...form.register('description')}
              style={inputStyle(false)}
            />
          </div>

          <div style={fieldStyle}>
            <label htmlFor="upstream-algorithm" style={labelStyle}>{t('gateway.upstreams.form.algorithm.label')}</label>
            <select id="upstream-algorithm" {...form.register('algorithm')} style={inputStyle(false)}>
              {UPSTREAM_ALGORITHMS.map((alg) => (
                <option key={alg} value={alg}>{alg}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label htmlFor="upstream-hashOn" style={labelStyle}>{t('gateway.upstreams.form.hashOn.label')}</label>
            <input
              id="upstream-hashOn"
              {...form.register('hashOn')}
              placeholder="ip / header / cookie"
              style={inputStyle(false)}
            />
          </div>

          <div style={fieldStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" {...form.register('enabled')} />
              {t('gateway.upstreams.form.enabled.label')}
            </label>
          </div>
        </div>
      )}

      {/* Health Check Tab */}
      {activeTab === 'healthcheck' && (
        <div role="tabpanel">
          <div style={fieldStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" {...form.register('healthcheckActive')} />
              {t('gateway.upstreams.form.healthcheckActive.label')}
            </label>
          </div>
          <div style={fieldStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" {...form.register('healthcheckPassive')} />
              {t('gateway.upstreams.form.healthcheckPassive.label')}
            </label>
          </div>
        </div>
      )}

      {/* JSON Tab */}
      {activeTab === 'json' && (
        <div role="tabpanel">
          <JsonEditor value={watchedValues} readOnly height={280} ariaLabel={t('gateway.shared.json.preview')} />
        </div>
      )}
    </FormDialog>
  );
}
